<template>
  <div class="flex max-w-2xl flex-col gap-3">
    <div class="flex items-start gap-4 rounded-lg border p-4">
      <span class="flex size-9 shrink-0 items-center justify-center rounded-md bg-accent text-accent-foreground">
        <MailIcon class="size-4.5" aria-hidden="true" />
      </span>
      <div class="flex flex-1 flex-col gap-0.5">
        <Label for="setup-email-login" class="text-base font-semibold">{{ t('admin.setup.login.email.title') }}</Label>
        <p class="text-sm leading-normal text-muted-foreground">
          {{ t('admin.setup.login.email.description') }}
        </p>
      </div>
      <Checkbox id="setup-email-login" v-model="emailLoginRef" class="mt-1 size-5" data-test-id="setup-email-login" />
    </div>

    <div class="flex flex-col gap-4 rounded-lg border p-4" :class="{ 'border-2 border-primary': ssoWantedRef }">
      <div class="flex items-start gap-4">
        <span class="flex size-9 shrink-0 items-center justify-center rounded-md bg-accent text-accent-foreground">
          <BuildingIcon class="size-4.5" aria-hidden="true" />
        </span>
        <div class="flex flex-1 flex-col gap-0.5">
          <Label for="setup-saml-login" class="text-base font-semibold">{{ t('admin.setup.login.sso.title') }}</Label>
          <p class="text-sm leading-normal text-muted-foreground">
            {{ t('admin.setup.login.sso.description') }}
          </p>
        </div>
        <Checkbox id="setup-saml-login" v-model="ssoWantedRef" class="mt-1 size-5" data-test-id="setup-saml-login" />
      </div>

      <template v-if="ssoWantedRef">
        <fieldset class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <legend class="sr-only">{{ t('admin.setup.login.sso.modeLegend') }}</legend>
          <label
            v-for="mode in SSO_MODES"
            :key="mode"
            class="flex cursor-pointer items-start gap-2.5 rounded-md border p-3 has-[:focus-visible]:ring-[3px] has-[:focus-visible]:ring-ring/50"
            :class="ssoModeRef === mode ? 'border-primary bg-accent' : 'border-border'"
          >
            <input
              v-model="ssoModeRef"
              type="radio"
              name="setup-sso-mode"
              :value="mode"
              class="mt-0.5 size-4 accent-primary"
              :data-test-id="`setup-sso-mode-${mode}`"
            />
            <span class="flex flex-col gap-0.5">
              <span class="text-sm font-semibold">{{ t(`admin.setup.login.sso.${mode}.title`) }}</span>
              <span class="text-xs leading-snug text-muted-foreground">
                {{ t(`admin.setup.login.sso.${mode}.description`) }}
              </span>
            </span>
          </label>
        </fieldset>

        <div class="flex flex-col gap-2 rounded-md bg-muted p-3">
          <span class="text-sm font-semibold">{{ t('admin.setup.login.sp.title') }}</span>
          <dl class="grid grid-cols-1 gap-x-3 gap-y-1 font-mono text-xs leading-normal sm:grid-cols-[auto_1fr]">
            <dt class="text-muted-foreground">{{ t('admin.setup.login.sp.entityId') }}</dt>
            <dd class="m-0 break-all" data-test-id="setup-sp-entity-id">{{ entityIdRef }}</dd>
            <dt class="text-muted-foreground">{{ t('admin.setup.login.sp.callback') }}</dt>
            <dd class="m-0 break-all">{{ callbackRef }}</dd>
          </dl>
          <template v-if="ssoModeRef === 'itTeam'">
            <Button
              variant="outline"
              size="sm"
              class="w-fit"
              :aria-expanded="isEmailShownRef"
              aria-controls="setup-sso-email"
              data-test-id="setup-sso-prepare-email"
              @click="isEmailShownRef = !isEmailShownRef"
            >
              <SendIcon class="size-4" aria-hidden="true" />
              {{ t('admin.setup.login.sp.mailto') }}
            </Button>
            <!-- Affiché dans la page : un lien mailto seul ne fait rien sans messagerie installée (webmail). -->
            <div
              v-if="isEmailShownRef"
              id="setup-sso-email"
              class="flex flex-col gap-3 rounded-md border bg-background p-3"
              data-test-id="setup-sso-email"
            >
              <div class="flex flex-col gap-1.5">
                <Label for="setup-sso-email-subject">{{ t('admin.setup.login.sp.subjectLabel') }}</Label>
                <Input id="setup-sso-email-subject" :model-value="emailRef.subject" readonly />
              </div>
              <div class="flex flex-col gap-1.5">
                <Label for="setup-sso-email-body">{{ t('admin.setup.login.sp.bodyLabel') }}</Label>
                <Textarea id="setup-sso-email-body" :model-value="emailRef.body" readonly rows="9" class="text-xs" />
              </div>
              <div class="flex flex-wrap items-center gap-3">
                <Button size="sm" data-test-id="setup-sso-copy-email" @click="copyEmail">
                  <CheckIcon v-if="isCopiedRef" class="size-4" aria-hidden="true" />
                  <CopyIcon v-else class="size-4" aria-hidden="true" />
                  {{ isCopiedRef ? t('admin.setup.login.sp.copied') : t('admin.setup.login.sp.copy') }}
                </Button>
                <a :href="mailtoRef" class="text-sm font-medium">{{ t('admin.setup.login.sp.openMailApp') }}</a>
              </div>
              <p role="status" class="sr-only">{{ isCopiedRef ? t('admin.setup.login.sp.copied') : '' }}</p>
            </div>
          </template>
        </div>

        <div v-if="ssoModeRef === 'self'" class="flex flex-col gap-5">
          <div class="flex flex-col gap-2">
            <Label for="setup-idp-url">{{ t('admin.setup.login.idpUrl.label') }}</Label>
            <Input
              id="setup-idp-url"
              v-model="form.SAML_IDP_SSO_URL"
              type="url"
              placeholder="https://"
              data-test-id="setup-idp-url-input"
            />
          </div>
          <div class="flex flex-col gap-2">
            <Label for="setup-idp-certificate">{{ t('admin.setup.login.certificate.label') }}</Label>
            <Textarea
              id="setup-idp-certificate"
              v-model="form.SAML_CERTIFICATE"
              rows="5"
              class="font-mono text-xs"
              placeholder="-----BEGIN CERTIFICATE-----"
              aria-describedby="setup-idp-certificate-description"
              data-test-id="setup-idp-certificate-input"
            />
            <p id="setup-idp-certificate-description" class="text-sm text-muted-foreground">
              {{ t('admin.setup.login.certificate.description') }}
            </p>
          </div>
        </div>
        <div class="flex flex-col gap-3 border-t pt-4" data-test-id="setup-sso-logo">
          <span class="text-sm font-semibold">{{ t('admin.setup.login.ssoLogo.label') }}</span>
          <!-- Même rendu que le bouton SSO de (auth)/login/page.vue -->
          <div class="flex flex-col gap-1.5 rounded-md bg-muted p-3" aria-hidden="true">
            <span class="text-xs text-muted-foreground">{{ t('admin.setup.login.ssoLogo.preview') }}</span>
            <div class="pointer-events-none" inert>
              <Button variant="outline" class="w-full !text-foreground" tabindex="-1">
                <img
                  v-if="ssoButtonLogoRef"
                  :src="ssoButtonLogoRef"
                  alt=""
                  class="!h-4 !w-4 shrink-0 rounded-[5px] !object-contain"
                />
                {{ t('auth.login.continueWith', { companyName: form.COMPANY_NAME }) }}
              </Button>
            </div>
          </div>
          <p class="text-sm text-muted-foreground" data-test-id="setup-sso-logo-state">
            {{ form.LOGO_SSO ? t('admin.setup.login.ssoLogo.custom') : t('admin.setup.login.ssoLogo.default') }}
          </p>
          <LogoUpload
            id="setup-logo-sso"
            v-model="form.LOGO_SSO"
            :fallback-src="form.LOGO_SMALL || undefined"
            :choose-label="t('admin.setup.login.ssoLogo.choose')"
            :remove-label="t('admin.setup.login.ssoLogo.reset')"
            :description="t('admin.setup.login.ssoLogo.description')"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { BuildingIcon, CheckIcon, CopyIcon, MailIcon, SendIcon } from 'lucide-vue-next'
import { computed, ref } from 'vue'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import LogoUpload from '@/features/admin/platform-setup/components/logo-upload.vue'
import type { SsoModeType } from '@/features/admin/platform-setup/hooks/use-platform-setup.hook'
import { usePlatformSetup } from '@/features/admin/platform-setup/hooks/use-platform-setup.hook'
import { useI18n } from '@/lib/i18n'

// Comme la page de connexion : le logo du bouton SSO, sinon le petit logo de la plateforme.
const ssoButtonLogoRef = computed(() => form.LOGO_SSO || form.LOGO_SMALL)

const SSO_MODES: SsoModeType[] = ['itTeam', 'self']

const { t } = useI18n()
const { form, emailLoginRef, ssoWantedRef, ssoModeRef } = usePlatformSetup()

// Mêmes valeurs que la stratégie SAML de l'API (app.ts) : elles dérivent de son adresse publique.
const apiUrl = ((import.meta as any).env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? ''
const entityIdRef = computed(() => `${apiUrl}/v1/auth/saml/metadata`)
const callbackRef = computed(() => `${apiUrl}/v1/auth/saml/callback`)

const emailRef = computed(() => {
  const platform = form.TITLE || form.COMPANY_NAME
  return {
    subject: t('admin.setup.login.sp.mailSubject', { platform }),
    body: t('admin.setup.login.sp.mailBody', { platform, entityId: entityIdRef.value, callback: callbackRef.value }),
  }
})
const mailtoRef = computed(
  () => `mailto:?subject=${encodeURIComponent(emailRef.value.subject)}&body=${encodeURIComponent(emailRef.value.body)}`,
)

const isEmailShownRef = ref(false)
const isCopiedRef = ref(false)

async function copyEmail() {
  try {
    await navigator.clipboard.writeText(`${emailRef.value.subject}\n\n${emailRef.value.body}`)
    isCopiedRef.value = true
    setTimeout(() => (isCopiedRef.value = false), 3000)
  } catch {
    // presse-papiers refusé : le texte reste sélectionnable dans les champs
  }
}
</script>
